import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Account, AccountFeatures, AccountsService, IAccount } from '@services/accounts.service';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { DatapointsDataService, IDataPoint } from '@services/datapoints-data.service';
import { IIntegratonSensor, IntegrationSensorsService } from '@services/integration-sensors.service';
import { IntegrationsService, IWebhookIntegration } from '@services/integrations.service';
import { ListView, ScrolledEventArgs, SelectEventArgs } from '@syncfusion/ej2-angular-lists';
import { combineLatest, firstValueFrom, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, take, takeUntil, tap } from 'rxjs/operators';
import { EditSensorDatapointComponent } from 'src/app/dialogs/edit-sensor-datapoint/edit-sensor-datapoint.component';
import { EditSensorComponent } from 'src/app/dialogs/edit-sensor/edit-sensor.component';
import { convertEpochToDateString } from '@functions/functions'
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { OfflineEvent } from '@services/events-data.service';
import { MatCheckbox } from '@angular/material/checkbox';
import { LinkAssetDatapointDialogData, LinkAssetDatapointsComponent } from 'src/app/dialogs/link-asset-datapoints/link-asset-datapoints.component';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { DatapointGraphComponent, DataPointGraphDialogData } from 'src/app/dialogs/datapoint-graph/datapoint-graph.component';
import { ComponentType } from '@angular/cdk/portal';
import { EditSensorNeuronComponent } from 'src/app/dialogs/edit-sensor-neuron/edit-sensor-neuron.component';
import { IRule, RuleDefinition, RulesDataService } from '@services/rules-data.service';
import { CreateRuleComponent, EditRuleDialogData } from 'src/app/dialogs/create-rule/create-rule.component';

@Component({
	selector: 'app-data-integrations-sensors',
	templateUrl: './data-integrations-sensors.component.html',
	styleUrls: ['./data-integrations-sensors.component.scss']
})
export class DataIntegrationsSensorsComponent implements OnInit, OnDestroy {

	/** Path to Sensor Images */
	public imagePathSensors(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.sensors).toLowerCase()
	}

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.selectedAccountGUID, imgTypes.organisationassets).toLowerCase()
	}


	/** Check user is an Account Admin */
	public isAdmin(site: boolean = false): boolean {
		try {
			if (site && !this.currentUser.AdminSite) {
				return false;
			}
			return (this.selectedAccount.Admin || this.currentUser.AdminSite) ?? false;
		} catch { return false; }
	}

	/** Route Organisation ID */
	public routeOrganisationID: string

	/** Selected Account GUID */
	public selectedAccountGUID: string

	/** Selected Account */
	public selectedAccount: IAccount

	/** Route Webhook ID */
	@Input() routeWebhookID: string

	/** Webhook Integration */
	public webhookIntegration: IWebhookIntegration

	/** Current User */
	public currentUser: IWebAppUser

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Array of Sensors */
	public sensorsList: IIntegratonSensor[];

	/** filtered Sensors List */
	public filteredSensorsList: IIntegratonSensor[];

	/** Selected Sensor */
	public selectedSensor: IIntegratonSensor | undefined;

	/** Sensors Datapoints */
	public sensorDatapoints: IDataPoint[]

	public sensorRules: IRule[] = []

	/** listviewInstance */
	@ViewChild('listviewInstance') public listviewInstance: ListView;

	/** cssClass */
	public cssClass: string = 'e-list-template';

	/** isMobile */
	public isMobile: boolean;

	/** sensorAssets */
	public sensorAssets: LevelAsset[] = [];

	/** sensorOfflineHistory */
	public sensorOfflineHistory: OfflineEvent[] = [];

	/** filterValue */
	public filterValue: string = ""

	// set the value to select an item based on mapped value at initial rendering
	private ShowSensorsKey: string = "showSensors"
	public showSensors: string = localStorage.getItem(this.ShowSensorsKey) || "1";

	// define the JSON of data for th eButton Group
	public BGSelection: {
		Id: string;
		Value: string;
		Text: string;
		Icon: string;
		Color: string;
	}[] = [
			{ Id: '1', Value: 'All', Text: 'Showing All Sensors', Icon: '', Color: '' },
			{ Id: '2', Value: 'Online', Text: 'Showing Only Online Sensors', Icon: 'wifi', Color: '' },
			{ Id: '3', Value: 'Offline', Text: 'Showing Only Offline Sensors', Icon: 'wifi_off', Color: '' },
			{ Id: '4', Value: 'Enabled', Text: 'Showing Only Enabled Sensors', Icon: 'toggle_on', Color: 'green' },
			{ Id: '5', Value: 'Disabled', Text: 'Showing Only Disabled Sensors', Icon: 'toggle_off', Color: 'red' }
		];

	/** topCheckbox */
	@ViewChild('topCheckbox') topCheckbox: MatCheckbox;

	/** Get AccountFeatures Enum */
	AccountFeatures = AccountFeatures;

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[];

	/**
	 * Default Constructor
	 * @param globalFunctions Global Functions
	 * @param authenticationService Authentication Services
	 * @param activatedRoute ActivatedRoute
	 * @param siteSubscriptions siteSubscriptions
	 * @param integrationSensorsService IntegrationSensorsService
	 * @param datapointsDataService datapointsDataService
	 * @param rulesDataService RulesDataService,
	 * @param assetsDataService AssetsDataService
	 * @param dialog MatDialog
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		public router: Router,
		private activatedRoute: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private integrationSensorsService: IntegrationSensorsService,
		private integrationsService: IntegrationsService,
		private datapointsDataService: DatapointsDataService,
		private rulesDataService: RulesDataService,
		private assetsDataService: AssetsDataService,
		public dialog: MatDialog,
	) {
		if (this.activatedRoute.parent) {
			combineLatest([
				this.authenticationService.currentUser$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.selectedAccount$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.activatedRoute.parent.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.activatedRoute.paramMap.pipe(takeUntil(this.finalise), distinctUntilChanged()),
				this.siteSubscriptions.hierarchicalData$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			]).pipe(
				// Ensure that subscriptions are not null or undefined
				filter(([user, account, parentParams, params, hierarchicalData]) => !!user && !!account && !!parentParams && !!params && !!hierarchicalData)
			).subscribe({
				next: async ([user, account, parentParams, params, hierarchicalData]) => {
					this.currentUser = <IWebAppUser>user;
					this.selectedAccount = account;
					this.selectedAccount = this.siteSubscriptions.normalizeFeatures(this.selectedAccount) as Account;
					this.selectedAccountGUID = this.selectedAccount.GUID;
					this.routeOrganisationID = parentParams.get('organisation') ?? ""
					this.routeWebhookID = params.get('integrationID') ?? "";
					this.hierarchy = JSON.parse(hierarchicalData);

					try {
						const integrations = await firstValueFrom(
							this.integrationsService.getWebhookIntegrations$(this.selectedAccountGUID, this.routeOrganisationID, this.routeWebhookID)
								.pipe(
									filter((integrations) => !!integrations)
								)
						);
						this.webhookIntegration = integrations
						this.GetSensors(this.routeWebhookID);
					} catch (error) {
						this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationService.getWebhookIntegrations$`, false);
					}

					// this.GetSensors(this.routeWebhookID);
				},
				error: (error) => { this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → Error in one of the combined subscriptions`, false); }
			});
		}
	}

	/**
	 * checkFeature
	 * @param feature feature   
	 * @returns 
	 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.selectedAccount && this.selectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.selectedAccount)
			// return this.selectedAccount.Features.some((x: any) => x.FeatureID == feature && x.Enabled);
		}
		return false;
	}

	/**
	 * ngOnInit
	 */
	ngOnInit(): void {
		this.isMobile = window.matchMedia('(max-width:550px)').matches;
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * convertEpoch
	 * @param epoch
	 * @returns
	 */
	convertEpoch(epoch: number): string {
		/** Use the Global Function */
		return convertEpochToDateString(epoch);
	}

	/**
	 * toggleButtonGroupFilter
	 * @param show 
	 */
	toggleButtonGroupFilter(show: string): void {
		this.showSensors = show;
		localStorage.setItem(this.ShowSensorsKey, this.showSensors);
		this.applyFilter()
	}

	/**
	 * showSensorText
	 */
	get showSensorText(): string {
		return this.BGSelection.find(x => x.Id === this.showSensors)?.Text ?? '';
	}

	/**
	 * applyFilter
	 * @param arg
	 */
	applyFilter(value: string = this.filterValue) {
		this.filterValue = value.toLowerCase();

		var ddValue = this.showSensors
		var filterList

		if (ddValue == "1") {
			filterList = this.sensorsList;
		} else if (ddValue == "2") {
			filterList = this.sensorsList!.filter((sensor) => sensor.Offline == false);
		} else if (ddValue == "3") {
			filterList = this.sensorsList!.filter((sensor) => sensor.Offline == true);
		} else if (ddValue == "4") {
			filterList = this.sensorsList!.filter((sensor) => sensor.Enabled == true);
		} else if (ddValue == "5") {
			filterList = this.sensorsList!.filter((sensor) => sensor.Enabled == false);
		}

		if (this.filterValue != "") {
			this.filteredSensorsList = filterList!.filter(sensor =>
				sensor.Name.toLowerCase().includes(this.filterValue) ||
				sensor.Description.toLowerCase().includes(this.filterValue) ||
				sensor.PublicSensorID.toLowerCase().includes(this.filterValue)
			);
		} else { this.filteredSensorsList = filterList ?? []; }

	}

	/**
	 * GetSensors
	 * @param routeWebhookID
	 * @param clearFirst
	 */
	async GetSensors(routeWebhookID: string, clearFirst: boolean = true) {
		try {
			// Call the Account Service to get the Organisations
			const sensors = await firstValueFrom(
				this.integrationSensorsService.getIntegrationSensors$(this.selectedAccountGUID, this.routeOrganisationID, routeWebhookID, {}).pipe(
					tap(() => {
						this.globalFunctions.displaySpinner(true);
						if (clearFirst) {
							this.sensorsList = []
							this.filteredSensorsList = []
							this.selectedSensor = undefined
						}
						this.sensorDatapoints = []
					}),
					filter((sensors) => !!sensors)
				)
			);

			if (sensors) {
				this.sensorsList = <IIntegratonSensor[]>sensors
				this.applyFilter(this.filterValue)

				if (this.selectedSensor) {
					this.selectedSensor = this.sensorsList?.find(x => x.PublicSensorID == this.selectedSensor?.PublicSensorID)!
					this.sensorDatapoints = this.selectedSensor.Datapoints;
				}
			} else {
				this.sensorsList = []
				this.filteredSensorsList = []
				this.selectedSensor = undefined
			}
		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationSensorsService.getIntegrationSensors$`, false)
			}
			this.globalFunctions.displaySpinner(false);
			this.sensorsList = []
			this.filteredSensorsList = []
			this.selectedSensor = undefined
		} finally {
			this.globalFunctions.displaySpinner(false)
		}
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);

	}

	/**
	 * onListScroll
	 * @param args
	 */
	onListScroll(args: ScrolledEventArgs): void {
	}

	/**
	 * actionBegin
	 * @param args
	 */
	actionBegin(args: any) {
		// this.globalFunctions.displaySpinner(true);
	}

	/**
	 *
	 * @param args onActionComplete
	 */
	onActionComplete(args: any) {
		this.globalFunctions.displaySpinner(false);
	}

	/**
	 * selectSensor
	 * @param args
	 */
	selectSensor(args: SelectEventArgs) {
		this.selectedSensor = <IIntegratonSensor>(<any>this.listviewInstance.localData[args.index]);
		this.sensorDatapoints = this.selectedSensor.Datapoints;
		//get sensor assets and offline history
		this.getSensorDetails(this.selectedSensor);
	}


	/**
	 * editSensor
	 * @param editSensor 
	 * @param isNeuron 
	 * @returns 
	 */
	async editSensor(editSensor: IIntegratonSensor, isNeuron: boolean = false) {

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...editSensor };

		const component: ComponentType<any> = isNeuron
			? EditSensorNeuronComponent
			: EditSensorComponent;

		const dialogConfig: MatDialogConfig = {
			data: _edit
		};

		if (isNeuron) {
			dialogConfig.data = {
				sensor: _edit,
				webhook: this.webhookIntegration
			};

			dialogConfig.width = '900px';
			dialogConfig.maxWidth = '95vw';
		};

		// Display the dialog box
		const dialogRef = this.dialog.open(component, dialogConfig);
		const result = await firstValueFrom(dialogRef.afterClosed());
		if (!result) { return; }

		try {
			this.globalFunctions.displaySpinner(true)
			// Update the original Asset with the result
			Object.assign(_edit, result);
			const response = await firstValueFrom(
				this.integrationSensorsService.patchIntegrationSensor$(
					this.selectedAccountGUID, this.routeOrganisationID, this.routeWebhookID, _edit).pipe(
						filter((response) => !!response)
					)
			);
			if (response) {
				Object.assign(editSensor, response[0]);
				//this.GetDataPoints(this.asset);
				this.globalFunctions.DisplaySnackBar({
					message: "Sensor Details Updated",
					type: SnackType.Info,
					duration: 1000
				});
				this.getSensorDetails(editSensor);
			}
		}
		catch (error) {
			console.log(error);
			this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationSensorsService.patchIntegrationSensor$`, false);
		} finally {
			this.globalFunctions.displaySpinner(false)
		}
	}

	/**
	 * Edit the Datapoint
	 * @param editDatapoint
	 */
	async editDataPoint(editDatapoint: IDataPoint) {

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...editDatapoint };

		// Display the dialog box
		const dialogRef = this.dialog.open(EditSensorDatapointComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		const result = await firstValueFrom(dialogRef.afterClosed());
		if (!result) { return; }

		try {
			// Check we have a result to work with
			this.globalFunctions.displaySpinner(true)
			Object.assign(_edit, result);
			const response = await firstValueFrom(
				this.datapointsDataService.patchDataPoint$(this.selectedAccountGUID, this.routeOrganisationID, _edit).pipe(
					take(1), distinctUntilChanged(),
					filter((response) => !!response)
				)
			);
			if (response) {
				Object.assign(editDatapoint, result);
				this.globalFunctions.DisplaySnackBar({
					message: "DataPoint Details Updated",
					type: SnackType.Info,
					duration: 1000
				});
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.DatapointsDataService.patchDataPoint$`, false);

		} finally {
			this.globalFunctions.displaySpinner(false)
		}
	}

	/**
	 * viewDatapointGraph
	 * @param viewDatapoint 
	 */
	viewDatapointGraph(viewDatapoint: IDataPoint): void {
		// Create a 'Local'/shallow copy of the Asset Information
		var _DP = { ...viewDatapoint };

		const data: DataPointGraphDialogData = {
			datapoint: _DP,
			accountGUID: this.selectedAccountGUID,
			organisationUID: this.routeOrganisationID
		};

		// Display the dialog box
		const dialogRef = this.dialog.open(
			DatapointGraphComponent,
			{
				data: data,
			}
		);
	}


	/**
	 * goToFunctionalLocation
	 * @param asset asset
	 */
	goToFunctionalLocation(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`/${this.selectedAccountGUID}/organisation/${this.routeOrganisationID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
	}

	/**
	 * goToEvent
	 * @param event event
	 */
	goToEvent(event: OfflineEvent) {
		this.router.navigate([`/${this.selectedAccountGUID}/organisation/${this.routeOrganisationID}/event/${event.OfflineEventID}`],
			{
				queryParams: { eventType: 1 }
			}
		);
	}

	/**
	 * enableAllSensors
	 * @param enable enable
	 */
	enableAllSensors(enable: boolean, enableDPs: boolean = false) {

		this.integrationsService.enableAllSensorsForWebhookIntegration$(this.selectedAccountGUID, this.routeOrganisationID, this.routeWebhookID, enable, enableDPs).pipe(
			take(1), distinctUntilChanged(),
			filter((response) => !!response)
		).subscribe({
			next: (response) => {
				this.GetSensors(this.routeWebhookID);
				this.globalFunctions.DisplaySnackBar({
					message: enable ? "Sensors Enabled" : "Sensors Disabled",
					type: SnackType.Info,
					duration: 1000
				});
			},
			error: (error) => { this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationSensorsService.patchIntegrationSensor$`, false) },
			complete: () => { this.globalFunctions.displaySpinner(false) }
		});
	}

	/**
	 * toggleAllSensors
	 * @param event event
	 */
	toggleAllSensors(event: any) {
		this.filteredSensorsList?.forEach(
			sensor => sensor._Selected = event.checked
		)
	}

	/**
	 * refreshSensors
	 */
	refreshSensors() {
		this.GetSensors(this.routeWebhookID, false)
	}

	/** allSensorsSelected */
	get allSensorsSelected(): boolean {
		return this.sensorsList?.every(s => s._Selected) ?? false;
	}

	/** hasSelectedSensors */
	get hasSelectedSensors(): boolean {
		return this.filteredSensorsList?.some(s => s._Selected) ?? false;
	}

	/**
	 * enableSelectedSensors
	 * @param enable 
	 * @param enableDPs 
	 * @returns 
	 */
	enableSelectedSensors(enable: boolean, enableDPs: boolean = false) {

		const list: number[] = this.filteredSensorsList
			?.filter(x => x._Selected)
			.map(x => x.SensorID)
			?? [];
		let SensorIDList: string = list.toString();

		if (!SensorIDList) { return; }

		this.integrationsService.enableAllSensorsForWebhookIntegration$(
			this.selectedAccountGUID, this.routeOrganisationID, this.routeWebhookID, enable, enableDPs, SensorIDList).pipe(
				take(1), distinctUntilChanged(),
				filter((response) => !!response)
			).subscribe({
				next: (response) => {
					this.GetSensors(this.routeWebhookID);
					this.globalFunctions.DisplaySnackBar({
						message: enable ? "Sensors Enabled" : "Sensors Disabled",
						type: SnackType.Info,
						duration: 1000
					});
				},
				error: (error) => { this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationSensorsService.patchIntegrationSensor$`, false) },
				complete: () => { this.globalFunctions.displaySpinner(false) }
			});
	}

	/**
	 * getSensorDetails
	 * @param sensor sensor 
	 */
	async getSensorDetails(sensor: IIntegratonSensor) {

		this.globalFunctions.displaySpinner(true);

		try {
			this.sensorRules = [];
			const rules = await firstValueFrom(
				this.rulesDataService.getRulesBySensorID$(this.selectedAccountGUID, this.routeOrganisationID, sensor.WebhookSensorID)
					.pipe(
						filter((rules) => !!rules)
					)
			);
			if (rules) {
				// Assign the sensor rules to the component property
				this.sensorRules = rules;
			}
		} catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.rulesDataService.getRulesBySensorID$`, false)
			}
		}

		try {
			this.sensorAssets = [];
			this.sensorOfflineHistory = [];
			const sensorDetails = await firstValueFrom(
				this.integrationSensorsService.getIntegrationSensorDetails$(this.selectedAccountGUID, this.routeOrganisationID, sensor.SensorID)
					.pipe(
						filter((sensorDetails) => !!sensorDetails)
					)
			);
			if (sensorDetails) {
				// Assign the sensor details to the component properties
				const data = sensorDetails[0];
				this.sensorAssets = this.parseJsonSafe(data.LinkedAssets, []);
				this.sensorOfflineHistory = this.parseJsonSafe(data.OfflineHistory, []);
			}
		} catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationSensorsService.getIntegrationSensorDetails$`, false)
			}
		}

		this.globalFunctions.displaySpinner(false);
	}

	async getAssetForRule(rule: IRule) {

		try {

			const ruleAsset = await firstValueFrom(
				this.assetsDataService.getOrganisationAssets$(this.selectedAccountGUID, this.routeOrganisationID, rule.AssetID)
					.pipe(
						filter((ruleAsset) => !!ruleAsset)
					)
			);
			if (ruleAsset) {
				this.onEditRule(rule, ruleAsset);
			}

		} catch (error: any) {
			if (error.status == 404) { }
			else { this.globalFunctions.HandleError(error, "Error: AssetsDataService.getOrganisationAssets$", false); }
		}
	}

	/**
 * Converts int of total minutes to [HH,MM] string array
 * @param totalMinutes
 * @returns
 */
	convertMinutesToHHMM(totalMinutes: number): string[] {
		const hours = Math.floor(totalMinutes / 60);
		const minutes = totalMinutes % 60;
		const hoursString = hours.toString().padStart(2, '0');
		const minutesString = minutes.toString().padStart(2, '0');
		return [hoursString, minutesString]
	}

	/**
	 * Converts the HH:MM format required for the trigger time to an int of
	 * total minutes
	 * @param timeStr
	 * @returns
	 */
	timeToMinutes(timeStr: string): number {
		const [hours, minutes] = timeStr.split(':').map(Number);
		const totalMinutes = hours * 60 + minutes;
		return totalMinutes;
	}

	/**
		 * Edit an existing rule
		 * @param rule
		 * @param event
		 */
	onEditRule(rule: IRule, asset: LevelAsset) {

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...rule };

		let time: string = this.convertMinutesToHHMM(_edit.TriggerTime)[0] + ":" + this.convertMinutesToHHMM(_edit.TriggerTime)[1]

		let dialogData = <EditRuleDialogData>{
			assetItem: asset,
			ruleItem: _edit,
			triggerTime: time,
			comparison: _edit.RuleText.match(/AND|OR/)?.[0] || ""
		}

		const dialogRef = this.dialog.open(CreateRuleComponent, {
			data: dialogData
		});

		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(async result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			if (result.ruleEnabled == null) {
				rule.RuleEnabled = false;
			} else {
				rule.RuleEnabled = result.ruleEnabled
			}

			if (result.CMMSEnabled == null) {
				rule.CMMSEnabled = false;
			} else {
				rule.CMMSEnabled = result.CMMSEnabled
			}

			rule.RuleDescription = result.ruleDescription
			rule.RuleMessage = result.ruleMessage
			rule.TriggerTime = this.timeToMinutes(result.ruleTime)

			//split the rule into the rule-definition composition

			if (rule.RuleDef) {
				let data1;
				if (result.condition1 != '~' && result.condition1 != '!~') {
					data1 = {
						RuleDefID: rule.RuleDef[0].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 1,
						DatapointID: result.dataPoint1,
						Limit: result.value1,
						Operator: result.condition1
					}
				} else {
					data1 = {
						RuleDefID: rule.RuleDef[0].RuleDefID,
						RuleID: rule.RuleID,
						Condition: 1,
						DatapointID: result.dataPoint1,
						Limit: result.between1value1,
						LowerLimit: result.between1value2,
						Operator: result.condition1
					}
				}

				let definition1: RuleDefinition = data1;

				let definition2 = undefined;
				/** if we already had another definition */
				if (result.dataPoint2 && rule.RuleDef[1]) {
					let data2;
					if (result.condition2 != '~' && result.condition2 != '!~') {
						data2 = {
							RuleDefID: rule.RuleDef[1].RuleDefID,
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.value2,
							Operator: result.condition2
						}
					} else {
						data2 = {
							RuleDefID: rule.RuleDef[1].RuleDefID,
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.between2value1,
							LowerLimit: result.between2value2,
							Operator: result.condition2
						}
					}
					definition2 = data2;
					/** if a new definition is being added */
				} else if (result.dataPoint2 && !rule.RuleDef[1]) {
					let data2;
					if (result.condition2 != '~' && result.condition2 != '!~') {
						data2 = {
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.value2,
							Operator: result.condition2
						}
					} else {
						data2 = {
							RuleID: rule.RuleID,
							Condition: 2,
							DatapointID: result.dataPoint2,
							Limit: result.between2value1,
							LowerLimit: result.between2value2,
							Operator: result.condition2
						}
					}
					definition2 = data2;
				}

				if (definition2) {
					rule.RuleText = "[1] " + result.comparison + " [2]";
					rule.RuleDef = [definition1, definition2];

				} else {
					rule.RuleText = "[1]"
					rule.RuleDef = [definition1]
				}
			}

			let UpdatedRule = await this.patchRule(rule)
			Object.assign(rule, UpdatedRule)
		});
	}

	/**
		 * patchRule
		 * @param rule
		 */
	async patchRule(rule: IRule): Promise<IRule> {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		try {
			const response = await firstValueFrom(
				this.rulesDataService.patchRule$(this.selectedAccountGUID, this.routeOrganisationID, rule.AssetID, rule, this.currentUser.UserID)
			);

			// console.log(sqlResult)
			this.globalFunctions.DisplaySnackBar({
				message: "Rule Updated",
				type: SnackType.Info,
				duration: 1000
			});

			return response

		} catch (error: any) {
			if (error.status != 404) {
				console.error(error)
				this.globalFunctions.HandleError(error, "this.accountsService.getAccountUsers$: Error retrieving Users")
			} else {
				// console.error(error.error);				
			}
			return {} as IRule
		}
		finally { }
	}

	private parseJsonSafe<T>(value: string | T, defaultValue: T): T {
		if (!value) {
			return defaultValue;
		}

		if (typeof value !== 'string') {
			return value;
		}

		try {
			return JSON.parse(value);
		}
		catch (error) {
			console.warn('Invalid JSON:', value, error);
			return defaultValue;
		}
	}

	/**
	 * linkAsset
	 */
	linkAsset() {

		let data: LinkAssetDatapointDialogData = {
			datapoints: this.sensorDatapoints,
			hierarchy: this.hierarchy,
			accountGUID: this.selectedAccountGUID,
			organisationUID: this.routeOrganisationID
		}

		// Display the dialog box
		const dialogRef = this.dialog.open(LinkAssetDatapointsComponent, {
			data: data,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			take(1)
			// filter(result => !!result)
		).subscribe(result => {
			if (this.selectedSensor) {
				this.getSensorDetails(this.selectedSensor);
			}
		});
	}




	/** makeRuleReadable */
	makeRuleReadable(rule: any): string {
		let result: string = "";
		if (rule.RuleDef && rule.RuleDef[0].DatapointID) {
			for (let i: number = 0; i < rule.RuleDef?.length; i++) {
				let SI: string = this.sensorDatapoints.find(dp => dp.DataPointID === rule.RuleDef[i].DatapointID)?.SI || '';
				if (rule.RuleDef[i].Operator != '~' && rule.RuleDef[i].Operator != '!~') {
					result += SI + rule.RuleDef[i].Operator + rule.RuleDef[i].Limit;
				} else {
					result += SI + " between " + rule.RuleDef[i].Limit + " and " + rule.RuleDef[i].LowerLimit;
				}
				if (i != rule.RuleDef.length - 1) {
					const comp = rule.RuleText.match(/AND|OR/)?.[0] || "";
					result += " " + comp + " ";
				}
			}
			result += " for " + rule.TriggerTime + " minutes";
		} else {
			result = rule.ReadableRule;
		}
		return result;
	}
}
